import { useEffect, useState } from 'react';
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Stack, Typography,
} from '@mui/material';

import { api } from '../api/client';
import type { PlanStop } from '../api/types';
import { formatClock } from '../util/time';

/**
 * What is known about a stop that is only a coordinate.
 *
 * A dropped pin has no place record, so the place panel has nothing to fetch
 * and the name sat in the round as dead text — "38.92708, -77.01883" between
 * two stops that both opened on a click, and the one somebody placed by hand
 * is exactly the one they are least sure about.
 *
 * Deliberately not the place panel: this shows only what can be known without
 * inventing a record — the coordinate, the times the round keeps there, and
 * whatever stands at that spot according to the same lookup that labels a pin
 * when it is dropped.
 */
export function PinnedStopDialog({
  stop, cityId, onClose, onCopied,
}: {
  /** The stop to describe, or null to keep the dialog shut. */
  stop: PlanStop | null;
  cityId: string | null;
  onClose: () => void;
  /** Told what to say after the coordinates are copied. */
  onCopied?: (message: string) => void;
}) {
  /*
   * `undefined` while the lookup is in flight and `null` once it has come
   * back with nothing, so the panel can tell "still asking" from "nothing
   * there" — two states that would otherwise both render as blank.
   */
  const [address, setAddress] = useState<string | null | undefined>(undefined);

  const lat = stop?.lat;
  const lng = stop?.lng;

  useEffect(() => {
    setAddress(undefined);
    if (!cityId || lat === undefined || lng === undefined) return;

    let current = true;
    api.describePlace(cityId, lat, lng)
      .then((found) => { if (current) setAddress(found.address || found.name || null); })
      .catch(() => { if (current) setAddress(null); });

    // The dialog may have been closed, or another pin opened, while the
    // lookup was in flight.
    return () => { current = false; };
  }, [cityId, lat, lng]);

  return (
    <Dialog open={Boolean(stop)} onClose={onClose} maxWidth="xs" fullWidth>
      {stop && (
        <>
          <DialogTitle sx={{ pb: 0.5 }}>
            {address ?? 'Pinned spot'}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              You placed this one on the map
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Stack spacing={1.25} sx={{ mt: 1 }}>
              {address === undefined && (
                <Typography variant="body2" color="text.secondary">
                  Looking up what is here…
                </Typography>
              )}
              {address === null && (
                <Typography variant="body2" color="text.secondary">
                  Nothing is mapped at this spot — it is a place on the road, not a venue.
                </Typography>
              )}
              <Divider />
              <Typography variant="body2">
                Arrive {formatClock(stop.arrivalTime)} · stay {stop.dwellMinutes} min
                {' '}· leave {formatClock(stop.departureTime)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {stop.lat.toFixed(5)}, {stop.lng.toFixed(5)}
              </Typography>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                void navigator.clipboard?.writeText(`${stop.lat}, ${stop.lng}`);
                onCopied?.('Coordinates copied');
              }}
            >
              Copy coordinates
            </Button>
            <Button onClick={onClose}>Close</Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
